import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { Router } from '@angular/router';
import { FeatureFlagService, FeatureFlag } from '../../../feature-flags';

interface SearchResult {
  category: string;
  title: string;
  description: string;
  route: string;
  icon: string;
  featureFlag?: FeatureFlag;
}

@Component({
  selector: 'app-search-modal',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatListModule,
    MatButtonModule
  ],
  templateUrl: './search-modal.html',
  styleUrl: './search-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchModal {
  private bottomSheetRef = inject(MatBottomSheetRef<SearchModal>);
  private router = inject(Router);
  private readonly flags = inject(FeatureFlagService);

  searchTerm = signal('');
  
  // Mock results - TODO: Implementar busca real integrada com features
  allResults: SearchResult[] = [
    {
      category: 'Consultas',
      title: 'Consultar Pessoa',
      description: 'Buscar pessoa por CPF, nome ou nome da mãe',
      route: '/consultas/pessoa',
      icon: 'group_search'
    },
    {
      category: 'Consultas',
      title: 'Consultar Veículo',
      description: 'Buscar veículo por placa, chassi ou renavam',
      route: '/consultas/veiculo',
      icon: 'car_rental'
    },
    {
      category: 'Chamados',
      title: 'Chamados 190',
      description: 'Ver chamados recebidos e em andamento',
      route: '/copom',
      icon: 'phone_in_talk'
    },
    {
      category: 'DF 360',
      title: 'Alertas DF 360',
      description: 'Ver alertas de reconhecimento facial e veicular',
      route: '/sentinela',
      icon: 'video_camera_front'
    },
    {
      category: 'Relatório',
      title: 'Novo RAP',
      description: 'Criar novo Relatório de Atendimento Policial',
      route: '/rap/selecao-tipo',
      icon: 'description'
    },
    {
      category: 'Viatura',
      title: 'Abastecimento',
      description: 'Solicitar abastecimento da viatura',
      route: '/viatura/abastecimento',
      icon: 'local_gas_station'
    },
    {
      category: 'Viatura',
      title: 'Abastecimento',
      description: 'Solicitar abastecimento da viatura',
      route: '/viatura/abastecimento',
      icon: 'gas_meter'
    },
    {
      category: 'Viatura',
      title: 'Vistoria',
      description: 'Vistoriar a viatura da guarnição',
      route: '/viatura/vistoria',
      icon: 'fact_check',
      featureFlag: 'viaturaVistoria',
    },
    {
      category: 'Viatura',
      title: 'Receber viatura',
      description: 'Registrar o recebimento da vistoria',
      route: '/viatura/vistoria/recebimento',
      icon: 'draw',
      featureFlag: 'viaturaVistoria',
    }
  ];

  filteredResults = signal<SearchResult[]>(this.resultadosVisiveis());

  private resultadosVisiveis(): SearchResult[] {
    return this.allResults.filter(
      (item) => !item.featureFlag || this.flags.isEnabled(item.featureFlag),
    );
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    const visiveis = this.resultadosVisiveis();

    if (!term.trim()) {
      this.filteredResults.set(visiveis);
      return;
    }

    const lowerTerm = term.toLowerCase();
    const filtered = visiveis.filter(
      result =>
        result.title.toLowerCase().includes(lowerTerm) ||
        result.description.toLowerCase().includes(lowerTerm) ||
        result.category.toLowerCase().includes(lowerTerm)
    );
    
    this.filteredResults.set(filtered);
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
    this.bottomSheetRef.dismiss();
  }

  close(): void {
    this.bottomSheetRef.dismiss();
  }
}
